import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/atoms';
import {
  BULK_UPLOAD_COLUMNS,
  BULK_UPLOAD_OPTIONAL_COLUMNS,
  BULK_UPLOAD_REQUIRED_COLUMNS,
  BULK_UPLOAD_SAMPLE_ROW,
  downloadBulkUploadSample,
  downloadBulkUploadTemplate,
} from '@/lib/constants/bulk-upload-template';
import { Download, FileSpreadsheet } from 'lucide-react';

export function BulkUploadTemplateSection() {
  return (
    <Card className="border shadow-none">
      <CardHeader className="gap-3 border-b pb-6">
        <div className="flex items-start gap-3">
          <div className="rounded-lg bg-primary/10 p-2 text-primary">
            <FileSpreadsheet className="size-5" />
          </div>
          <div className="space-y-1">
            <CardTitle className="text-lg">
              Start with the Sellyt template
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              The template keeps your columns in the exact order our importer
              expects, so validation passes on the first try.
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6 pt-6">
        <ul className="space-y-2 text-sm text-muted-foreground">
          <li className="flex gap-2">
            <span className="text-primary">•</span>
            <span>
              Use the provided template — it already contains every supported
              column.
            </span>
          </li>
          <li className="flex gap-2">
            <span className="text-primary">•</span>
            <span>Do not rename or remove any required columns.</span>
          </li>
          <li className="flex gap-2">
            <span className="text-primary">•</span>
            <span>Supported file formats: CSV and Excel (.xlsx).</span>
          </li>
          <li className="flex gap-2">
            <span className="text-primary">•</span>
            <span>Maximum upload size: 10MB.</span>
          </li>
          <li className="flex gap-2">
            <span className="text-primary">•</span>
            <span>Complete all required fields before uploading.</span>
          </li>
        </ul>

        <div className="flex flex-wrap gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={downloadBulkUploadTemplate}
          >
            <Download />
            Download Template
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={downloadBulkUploadSample}
          >
            <Download />
            Download Sample File
          </Button>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">Template structure</h3>
            <span className="text-sm text-muted-foreground">
              {BULK_UPLOAD_REQUIRED_COLUMNS.length} required fields ·{' '}
              {BULK_UPLOAD_OPTIONAL_COLUMNS.length} optional fields
            </span>
          </div>

          <div className="overflow-x-auto rounded-xl border">
            <Table>
              <TableHeader className="table-header-green">
                <TableRow>
                  {BULK_UPLOAD_COLUMNS.map((column) => {
                    const isRequired = (
                      BULK_UPLOAD_REQUIRED_COLUMNS as readonly string[]
                    ).includes(column);

                    return (
                      <TableHead
                        key={column}
                        className="whitespace-nowrap text-xs text-white uppercase"
                      >
                        {column}
                        {isRequired ? ' *' : '(optional)'}
                      </TableHead>
                    );
                  })}
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  {BULK_UPLOAD_COLUMNS.map((column) => (
                    <TableCell
                      key={column}
                      className="whitespace-nowrap text-sm text-muted-foreground"
                    >
                      {BULK_UPLOAD_SAMPLE_ROW[column] || '—'}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </div>

          <p className="text-sm text-muted-foreground">
            Product images are not part of the spreadsheet. Upload images in the
            Gallery tab first, then attach them to each product in the
            validation step after you upload your file.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
